import config from "@config";
import actionCreators from "@modules/hierarchy/actionCreator";
import {
  DeleteBranch,
  DiscardDraft,
  GetActiveHierarchyTree,
  GetActiveHierarchyTreeCsv,
  GetDraftHierarchyTree,
  GetDraftHierarchyTreeChangelog,
  GetDraftHierarchyTreeCsv,
  MoveBranch,
  RenameBranch,
  SubmitDraftHierarchyTree,
  ViewScopeChanges,
  SelectOrganizationalUnit,
  CreateHierarchyNotification,
  UpdateHierarchyNotification,
  GetNotificationContacts,
  GetHierarchyShiftPatterns,
  GetHierarchyNotificationsSummary,
  GetHierarchyShiftPatternSchema,
  CreateHierarchyShiftPattern,
  UpdateHierarchyShiftPattern,
  CutDepartment,
} from "@modules/hierarchy/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { all, call, put, takeEvery, race, take, spawn, select } from "redux-saga/effects";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";
import http from "@http";
import hierarchyChangelogSagas from "./change-log";
import departmentContactsSagas from "./department-contacts";
import hierarcchyChangesSagas from "./hierarchy-changes";
import {
  getselectedOrganisationalUnit,
  getDraftHierarchyTree,
  getAssignedShiftPattern,
} from "../selectors";

export function* onDiscardDraft({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    yield call(http.delete, `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft`);

    yield all([
      yield put(actionCreators.DiscardDraftSuccess(null)),
      yield put(sendSuccessMessage("DRAFT_DISCARDED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.DiscardDraftError(e.response));
  }
}

export function* onGetActiveHierarchyTree({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/active`,
      {
        params: {
          includeEmployeePositions: true,
        },
      },
    );

    yield put(actionCreators.getActiveHierarchyTreeSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getActiveHierarchyTreeError(e.response));
  }
}

export function* onGetActiveHierarchyTreeCsv({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/active/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield downloadCsvFile(response.data, "active-hierarchy.csv");
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onGetDraftHierarchyTree({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft`,
      {
        params: {
          includeEmployeePositions: true,
        },
      },
    );

    yield put(actionCreators.GetDraftHierarchyTreeSuccess(response.data));
    yield put(actionCreators.GetDraftHierarchyTreeChangelog({ clientId: payload.clientId }));
  } catch (e) {
    console.error("Saga error: ", e);
    // 404 means no draft, don't show errors
    if (e.response.status !== 404) {
      yield put(actionCreators.GetDraftHierarchyTreeError(e.response));
    } else {
      yield put(actionCreators.GetDraftHierarchyTreeSuccess(null));
    }
  }
}

export function* onGetDraftHierarchyTreeChangelog({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const page = payload.page || 1;
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/draft/log`,
      {
        params: {
          page,
          sort: "asc",
          limit: config.resultsPerPage,
        },
      },
    );

    yield put(actionCreators.GetDraftHierarchyTreeChangelogSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetDraftHierarchyTreeChangelogError(e.response));
  }
}

export function* getNotificationContacts({ payload }) {
  try {
    if (!payload || !payload.branch || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const clientId = payload.clientId;
    const branch = payload.branch;
    const page = payload.page || 1;

    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/notification`,
      {
        params: {
          includeInherited: true,
          page,
        },
      },
    );

    yield put(actionCreators.GetHierarchyNotificationContactsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetNotificationContactsError(e.response));
  }
}

export function* onCreateHierarchyNotification({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error("Invalid payload provided");
  }
  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/notification`,
      payload.data,
    );
    yield all([
      put(actionCreators.CreateHierarchyNotificationSuccess()),
      put(sendSuccessMessage("CONTACT_ADDED")),
      put(actionCreators.ToggleAddEditContactModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.CreateHierarchyNotificationError(e.response));
  }
}

type UpdateHierarchyNotificationRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  data: $Exact<{
    id: number,
    isCascading: boolean,
    notificationType: string,
    organisationalUnit: $Exact<{ id: number }>,
    recipient: $Exact<{ id: number }>,
  }>,
}>;

export function* onUpdateHierarchyNotification({
  payload,
}: Action<UpdateHierarchyNotificationRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error("Invalid payload provided");
  }

  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const response = yield call(
      http.put,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/notification/${payload.data.id}`,
      payload.data,
    );

    yield all([
      put(actionCreators.updateHierarchyNotificationSuccess(response.data)),
      put(sendSuccessMessage("CONTACT_UPDATED")),
      put(actionCreators.ToggleAddEditContactModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateHierarchyNotificationError(e.response));
  }
}

export function* onCreateHierarchyShiftPattern({
  payload,
}: Action<Object>): Generator<void, void, Object> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error("Invalid payload provided");
  }
  try {
    const branch = yield select(getselectedOrganisationalUnit);
    yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern`,
      payload.data,
    );
    yield all([
      put(actionCreators.CreateHierarchyShiftPatternSuccess()),
      put(sendSuccessMessage("SHIFT_PATTERN_ADDED")),
      put(actionCreators.ToggleAddEditShiftPatternModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.CreateHierarchyShiftPatternError(e.response));
  }
}

type UpdateHierarchyShiftPatternRequest = $Exact<{
  clientId: number,
  organisationalUnitId: number,
  data: Object,
}>;

export function* onUpdateHierarchyShiftPattern({
  payload,
}: Action<UpdateHierarchyShiftPatternRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.organisationalUnitId || !payload.data) {
    throw Error("Invalid payload provided");
  }

  try {
    const branch = yield select(getselectedOrganisationalUnit);
    const hierarchyShiftPattern = yield select(getAssignedShiftPattern);

    yield call(
      http.delete,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern/assignment/${payload.data.id}`,
      { data: { isCascading: hierarchyShiftPattern.isCascading } },
    );

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${payload.clientId}/hierarchy/organisational-unit/${payload.organisationalUnitId}/shift-pattern`,
      payload.data,
    );

    yield all([
      put(actionCreators.updateHierarchyShiftPatternSuccess(response.data)),
      put(sendSuccessMessage("SHIFT_PATTERN_UPDATED")),
      put(actionCreators.ToggleAddEditShiftPatternModal(false)),
      put(
        actionCreators.SelectOrganizationalUnit({
          clientId: payload.clientId,
          branch,
        }),
      ),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.UpdateHierarchyShiftPatternError(e.response));
  }
}

type HierarchyShiftPatternSchemaRequest = $Exact<{
  clientId: string,
}>;

export function* onGetHierarchyShiftPatternSchema({
  payload,
}: Action<HierarchyShiftPatternSchemaRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }
    const { clientId } = payload;
    const schema = yield parseSchema("HierarchyShiftPatternDTO", { clientId });
    yield put(actionCreators.GetHierarchyShiftPatternSchemaSuccess(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetHierarchyShiftPatternSchemaError(e.response));
  }
}

export function* onGetHierarchyNotificationsSummary({ payload }) {
  const clientId = payload.clientId || null;
  const branch = payload.branch || null;

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/notification/summary`,
    );

    yield put(actionCreators.GetHierarchyNotificationsSummarySuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetHierarchyNotificationsSummaryError(e.response));
  }
}

export function* getShiftPatterns({ payload }) {
  const clientId = payload.clientId || null;
  const branch = payload.branch || null;
  const page = payload.page || 1;

  try {
    const response = yield call(
      http.get,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/organisational-unit/${branch.data.id}/shift-pattern`,
      {
        params: {
          page,
        },
      },
    );

    yield put(actionCreators.GetHierarchyShiftPatternsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetHierarchyShiftPatternsError(e.response));
  }
}

export function* onSelectOrganizationalUnit({ payload }) {
  yield all([
    put(actionCreators.GetNotificationContacts(payload)),
    put(actionCreators.GetHierarchyShiftPatterns(payload)),
    put(actionCreators.GetHierarchyNotificationsSummary(payload)),
  ]);
}

export function* onGetDraftHierarchyTreeCsv({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/export`,
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield downloadCsvFile(response.data, "draft-hierarchy.csv");
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onSubmitDraftHierarchyTree({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/commit`,
    );
    yield put(actionCreators.getActiveHierarchyTreeSuccess(response.data));
    yield put(actionCreators.getDraftHierarchyTreeSuccess(null));
    yield put(actionCreators.setHierarchyModal(null));
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
    yield put(actionCreators.updateStep(0));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onViewScopeChanges({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/hierarchy-admin/client/${clientId}/hierarchy/active`,
      {
        params: {},
      },
    );
    yield put(actionCreators.getDraftHierarchyTreeError(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getDraftHierarchyTreeError(e.response));
  }
}

export function* onCutDepartment({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.changeId) {
      throw Error("Invalid payload provided");
    }

    const { branch, clientId } = payload;
    const tree = yield select(getDraftHierarchyTree);
    const uassignedNode = tree.structure.children.find(
      item => item.data.reference === "UNASSIGNED",
    );

    const response = yield call(
      http.post,
      `/api/v1/hierarchy-admin/client/${clientId}/hierarchy/draft/organisational-unit/${branch.data.id}/move`,
      {
        newParentId: uassignedNode.data.id,
      },
    );

    yield all([
      put(actionCreators.MoveBranchSuccess(payload.data)),
      put(actionCreators.GetDraftHierarchyTree({ clientId })),
      put(sendSuccessMessage("DEPARTMENT_CUT")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.CutDepartmentError(e.response));
  }
}

export function* ClientSaga() {
  yield all([
    // Hierarchy Admin
    takeEvery(DiscardDraft, onDiscardDraft),
    takeEvery(GetActiveHierarchyTree, onGetActiveHierarchyTree),
    takeEvery(GetActiveHierarchyTreeCsv, onGetActiveHierarchyTreeCsv),
    takeEvery(GetDraftHierarchyTree, onGetDraftHierarchyTree),
    takeEvery(GetDraftHierarchyTreeChangelog, onGetDraftHierarchyTreeChangelog),
    takeEvery(GetDraftHierarchyTreeCsv, onGetDraftHierarchyTreeCsv),
    takeEvery(SubmitDraftHierarchyTree, onSubmitDraftHierarchyTree),
    takeEvery(ViewScopeChanges, onViewScopeChanges),
    takeEvery(SelectOrganizationalUnit, onSelectOrganizationalUnit),
    takeEvery(GetHierarchyShiftPatterns, getShiftPatterns),
    takeEvery(GetNotificationContacts, getNotificationContacts),
    takeEvery(GetHierarchyNotificationsSummary, onGetHierarchyNotificationsSummary),
    takeEvery(CreateHierarchyNotification, onCreateHierarchyNotification),
    takeEvery(UpdateHierarchyNotification, onUpdateHierarchyNotification),
    takeEvery(GetHierarchyShiftPatternSchema, onGetHierarchyShiftPatternSchema),
    takeEvery(CreateHierarchyShiftPattern, onCreateHierarchyShiftPattern),
    takeEvery(UpdateHierarchyShiftPattern, onUpdateHierarchyShiftPattern),
    takeEvery(CutDepartment, onCutDepartment),
    spawn(hierarchyChangelogSagas),
    spawn(departmentContactsSagas),
    spawn(hierarcchyChangesSagas),
  ]);
}

export default ClientSaga;
